import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Button from "../components/Button";
import SolutionsSection from "../components/SolutionsSection";
import SEO from "../components/SEO";
import StructuredData, {
  createBreadcrumbSchema,
} from "../components/StructuredData";
import { seoData } from "../utils/seo";

// Viewport settings for better animation triggering
const viewportSettings = {
  once: true,
  amount: 0.1,
  margin: "0px 0px -50px 0px",
};

const About = () => {
  const coreValues = [
    {
      title: "Predictability",
      description:
        "Deliver consistent, reliable project outcomes through data-driven insights and forecasting.",
    },
    {
      title: "Transparency",
      description:
        "Ensure all stakeholders have real-time visibility into project status, budgets, and timelines.",
    },
    {
      title: "Connectivity",
      description:
        "Seamlessly connect teams, tools, and workflows across your entire real estate development ecosystem.",
    },
  ];

  const partners = [
    {
      name: "PropTech Sweden",
      logo: "/partners/64bfe4824f66a77c8c788fec_proptech sweden.jpg",
    },
    {
      name: "PropTech Denmark",
      logo: "/partners/64bfe4824f66a77c8c788fa0_PropTech Denmark Member.svg",
    },
    {
      name: "RICS Tech Partner",
      logo: "/partners/64bfe4824f66a77c8c78900e_RICS Tech Partner Logo - Black.png",
    },
    {
      name: "Urban Land Institute",
      logo: "/partners/64bfe4824f66a77c8c78906f_urban land institute logo.jpeg",
    },
    {
      name: "Plus Impact",
      logo: "/partners/64bfe4824f66a77c8c789006_Plusimpact_Logo_black.svg",
    },
    {
      name: "Microsoft for Startups",
      logo: "/partners/64bfe4824f66a77c8c788ecc_Microsoft-for-Startups.jpg",
    },
    {
      name: "Vinnova",
      logo: "/partners/64bfe4824f66a77c8c788ecd_vinnova_green_payoff_eng_rgb.png",
    },
    {
      name: "Startup Sweden",
      logo: "/partners/64bfe4824f66a77c8c788f9e_startup sweden logo-min.png",
    },
    {
      name: "NRGIZE",
      logo: "/partners/64bfe4824f66a77c8c788ec9_nrgize_cmyk.png",
    },
  ];

  const aboutSeo = seoData.about;
  const structuredData = [
    createBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "About", url: aboutSeo.url },
    ]),
  ];

  return (
    <div className="min-h-screen pt-20 sm:pt-24">
      <SEO
        title={aboutSeo.title}
        description={aboutSeo.description}
        keywords={aboutSeo.keywords}
        url={aboutSeo.url}
      />
      <StructuredData data={structuredData} />
      {/* Hero Section */}
      <section className="py-16 sm:py-20 lg:py-28 bg-gradient-to-br from-primary-50 to-white">
        <div className="container-custom px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto text-center"
          >
            <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              On a mission to accelerate sustainable and efficient real estate
              development with predictable outcomes for all stakeholders
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-16 sm:py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-3 sm:mb-4 px-4">
              Our Core Values
            </h2>
            <p className="text-center text-gray-600 mb-6 sm:mb-8 max-w-3xl mx-auto px-4 text-sm sm:text-base">
              These three pillars guide everything we do and every feature we
              build
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {coreValues.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={viewportSettings}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-lg p-6 sm:p-8 hover:shadow-xl transition-shadow"
                >
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solutions Section */}
      <SolutionsSection />

      {/* Partners Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-8 sm:mb-12 px-4">
              Partners that have trusted us through our journey
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 sm:gap-8">
              {partners.map((partner, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={viewportSettings}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="p-4 sm:p-6 flex items-center justify-center transition-all"
                >
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="max-h-12 sm:max-h-16 w-auto max-w-32 sm:max-w-40 object-contain"
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <div className="p-6 sm:p-8 lg:p-12">
              <div className="mb-6 sm:mb-8">
                <svg
                  className="w-10 h-10 sm:w-12 sm:h-12 text-primary-600"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
              </div>
              <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-gray-700 leading-relaxed mb-6 sm:mb-8">
                I've been working in the real estate industry for over 25 years,
                and I have a deep understanding of the market needs that
                Estabild addresses. Their effective and innovative solutions
                benefit the industry by increasing predictability, transparency,
                and connectivity. I am excited to support a solution that the
                market needs.
              </p>
              <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                <img
                  src="/avatars/64bfe4824f66a77c8c789052_per andersen avatar.png"
                  alt="Per Andersen"
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover flex-shrink-0"
                />
                <div>
                  <div className="font-bold text-gray-900 text-base sm:text-lg">
                    Per Andersen
                  </div>
                  <div className="text-gray-600 text-sm sm:text-base">
                    Investor and Advisory Board member,{" "}
                    <strong>Estabild</strong>
                  </div>
                  <div className="text-gray-600 text-sm sm:text-base">
                    Former Head of Real Estate,{" "}
                    <strong>KPMG and PwC Denmark</strong>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-24 lg:py-28 bg-primary-600 text-white">
        <div className="container-custom text-center px-4">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-8 sm:mb-10">
            Ready to streamline your project reporting?
          </h2>
          <Link to="/contact">
            <Button
              variant="secondary"
              className="text-lg sm:text-xl px-8 sm:px-10"
            >
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
